import type { Question } from "@circle-react/types/Live/QA/Question";
import type { Session } from "@circle-react/types/Live/QA/Session";
import {
  reactQueryDelete,
  reactQueryPatch,
  reactQueryPost,
  reactQueryPut,
} from "../helpers/backendRequestHelpers";
import { internalApi } from "../helpers/urlHelpers";

interface CreateQAQuestionParams {
  formData: Record<string, unknown>;
  roomId?: string | number;
}

const createQAQuestion = ({ roomId, formData }: CreateQAQuestionParams) =>
  reactQueryPost(
    internalApi.liveStreams.createQAQuestion({ roomId }),
    formData,
  );

interface RemoveQAQuestionParams {
  id?: string | number;
  roomId?: string | number;
}

const removeQAQuestion = ({ roomId, id }: RemoveQAQuestionParams) =>
  reactQueryDelete<Question, object>(
    internalApi.liveStreams.removeQAQuestion({ roomId, id }),
  );

interface ApproveQAQuestionParams {
  id?: string | number;
  roomId?: string | number;
}

const approveQAQuestion = ({ roomId, id }: ApproveQAQuestionParams) =>
  reactQueryPatch<Question, object>(
    internalApi.liveStreams.approveQAQuestion({ roomId, id }),
  );

interface DismissQAQuestionParams {
  id?: string | number;
  roomId?: string | number;
}

const dismissQAQuestion = ({ roomId, id }: DismissQAQuestionParams) =>
  reactQueryPatch<Question, object>(
    internalApi.liveStreams.dismissQAQuestion({ roomId, id }),
  );

interface MarkQAQuestionAsAnsweredParams {
  id?: string | number;
  roomId?: string | number;
}

const markQAQuestionAsAnswered = ({
  roomId,
  id,
}: MarkQAQuestionAsAnsweredParams) =>
  reactQueryPatch<Question, object>(
    internalApi.liveStreams.markQAQuestionAsAnswered({ roomId, id }),
  );

interface BringBackQAQuestionParams {
  id?: string | number;
  roomId?: string | number;
}

const bringBackQAQuestion = ({ roomId, id }: BringBackQAQuestionParams) =>
  reactQueryPatch<Question, object>(
    internalApi.liveStreams.bringBackQAQuestion({ roomId, id }),
  );

interface UpvoteQAQuestionParams {
  id?: string | number;
  roomId?: string | number;
}

const upvoteQAQuestion = ({ roomId, id }: UpvoteQAQuestionParams) =>
  reactQueryPatch<Question, object>(
    internalApi.liveStreams.upvoteQAQuestion({ roomId, id }),
  );

interface RemoveUpvoteQAQuestionParams {
  id?: string | number;
  roomId?: string | number;
}

const removeUpvoteQAQuestion = ({ roomId, id }: RemoveUpvoteQAQuestionParams) =>
  reactQueryPatch<Question, object>(
    internalApi.liveStreams.removeUpvoteQAQuestion({ roomId, id }),
  );

interface SpotlightQAQuestionParams {
  id?: string | number;
  roomId?: string | number;
}

const spotlightQAQuestion = ({ roomId, id }: SpotlightQAQuestionParams) =>
  reactQueryPatch<Question, object>(
    internalApi.liveStreams.spotlightQAQuestion({ roomId, id }),
  );

interface RemoveSpotlightQAQuestionParams {
  id?: string | number;
  roomId?: string | number;
}

const removeSpotlightQAQuestion = ({
  roomId,
  id,
}: RemoveSpotlightQAQuestionParams) =>
  reactQueryPatch<Question, object>(
    internalApi.liveStreams.removeSpotlightQAQuestion({ roomId, id }),
  );

interface UpdateQASessionParams {
  formData: Record<string, unknown>;
  roomId?: string | number;
}

const updateQASession = ({ roomId, formData }: UpdateQASessionParams) =>
  reactQueryPut<Session, Record<string, unknown>>(
    internalApi.liveStreams.updateQASession({ roomId }),
    formData,
  );

export const QAQuestionsApi = {
  createQAQuestion,
  removeQAQuestion,
  approveQAQuestion,
  dismissQAQuestion,
  markQAQuestionAsAnswered,
  bringBackQAQuestion,
  upvoteQAQuestion,
  removeUpvoteQAQuestion,
  spotlightQAQuestion,
  removeSpotlightQAQuestion,
  updateQASession,
};
